/**
 * Target Status Pips Config — nameplate-attached status markers on the
 * CURRENT target (spec: docs/plans/fx-grammar-specs/target-buff-bars.md).
 *
 * Same "defaults + dev override → merged" pattern as nameplate.config.ts /
 * classResource.config.ts: tune TARGET_STATUS_PIPS_DEV_OVERRIDE while
 * iterating, promote to defaults once happy. Consumers import
 * TARGET_STATUS_PIPS only.
 *
 * Style resolution is glyph/color based — the wire statusApplied payload
 * carries NO abilityId (StatusEffectService.ts broadcast), so ability-icon
 * lookup CANNOT work for plain status ids ('burn', 'agony', …). Colors come
 * from STATUS_AURA_CONFIG (read-only reuse — the status-color SSOT); glyphs
 * from the STATUS_GLYPHS map below. Unknown ids (e.g. buff-cast ids like
 * 'status_haste' entering via the local prediction lane) get the neutral
 * fallback style rather than being dropped — never hide problems.
 */
import { STATUS_AURA_CONFIG } from '../abilities/statusAura.config';

export interface TargetStatusPipsConfig {
  /** Kill switch — false hides the whole surface (declutter.enabled pattern). */
  enabled: boolean;
  /** Pip diameter in px. */
  pipSize: number;
  /** Horizontal gap between pips in px. */
  gap: number;
  /** Max pips drawn; the rest collapse into a `+N` overflow glyph. */
  maxPips: number;
  /** Fill color for status ids with no STATUS_AURA_CONFIG entry. */
  fallbackColor: number;
  /** Glyph for status ids with no STATUS_GLYPHS entry. */
  fallbackGlyph: string;
  /** Vertical offset in px below the target's HP-bar bottom edge. */
  underBarOffsetY: number;
  /** ActiveStatusIndex re-query throttle (ms). Draw runs per-frame; the
   *  status list is only re-read at this cadence (getStatusEntries
   *  allocates). This is also the visual-expiry latency bound — the index
   *  expires entries at query time, so a pip disappears within one
   *  interval. Fade math is NOT throttled: it runs per-frame off the
   *  cached entries' expiresAt, so the ramp is smooth between polls. */
  queryIntervalMs: number;
  /** Duration-fade window (ms) — the spec's "alpha fade over the final 3s".
   *  A pip holds full alpha while remaining time >= this window (clamped
   *  to the status's total durationMs so short statuses still start at
   *  full alpha), then ramps linearly toward fadeFloorAlpha at expiry.
   *  0 disables the fade (all pips full alpha). */
  fadeWindowMs: number;
  /** Alpha floor the fade ramps down to as expiry nears — never 0, so a
   *  nearly-expired status stays legible until the index drops it. */
  fadeFloorAlpha: number;
  /** Font size for the `+N` overflow tag. */
  overflowFontSize: number;
}

export const TARGET_STATUS_PIPS_DEFAULTS: TargetStatusPipsConfig = {
  enabled: true,
  pipSize: 7,
  gap: 3,
  maxPips: 8,
  fallbackColor: 0xcccccc,
  fallbackGlyph: '●',
  underBarOffsetY: 2,
  queryIntervalMs: 150,
  fadeWindowMs: 3000,
  fadeFloorAlpha: 0.35,
  overflowFontSize: 9,
};

/** Partial override layered on top of the defaults — leave `{}` to ship
 *  production defaults. Example: `{ maxPips: 4, pipSize: 9 }`. */
export const TARGET_STATUS_PIPS_DEV_OVERRIDE: Partial<TargetStatusPipsConfig> = {};

export const TARGET_STATUS_PIPS: TargetStatusPipsConfig = {
  ...TARGET_STATUS_PIPS_DEFAULTS,
  ...TARGET_STATUS_PIPS_DEV_OVERRIDE,
};

/**
 * Status id → glyph. Presentation vocabulary for anywhere a status needs a
 * character mark (phase-2 TargetInfoPanel chips reuse this). Keys mirror
 * STATUS_AURA_CONFIG; ids absent here fall back to cfg.fallbackGlyph.
 */
export const STATUS_GLYPHS: Record<string, string> = {
  burn: '🔥',
  poison: '☠',
  slow: '🐌',
  frozen: '❄',
  stun: '💫',
  regen: '✚',
  // Warlock affliction family — immolate is a true burn (honest alias,
  // matching its STATUS_AURA_CONFIG treatment).
  agony: '🩸',
  corruption: '🌀',
  immolate: '🔥',
  unstable_affliction: '☣',
  curse_of_elements: '🌩',
  heal_over_time_100: '✚',
  buff_speed_60: '⚡',
  buff_fortitude_100: '♥',
  buff_titans_allstats: '✦',
  buff_attack_10: '⚔',
  buff_defense_10: '◆',
  buff_experience_2x: '★',
};

export interface PipStyle {
  color: number;
  glyph: string;
}

/** Resolve a status id to its pip presentation (color + glyph), with the
 *  neutral fallback for unknown ids. Pure — safe in tests and layout code. */
export function resolvePipStyle(
  statusId: string,
  cfg: TargetStatusPipsConfig = TARGET_STATUS_PIPS,
): PipStyle {
  return {
    color: STATUS_AURA_CONFIG[statusId]?.color ?? cfg.fallbackColor,
    glyph: STATUS_GLYPHS[statusId] ?? cfg.fallbackGlyph,
  };
}
